import { Loader2 } from "lucide-react";
import { useState } from "react";

import { Button, type ButtonProps } from "@/components/ui/button";
import type { ConfirmModalDescriptor } from "@/services/simple-modal/simple-modal-types";

export interface ConfirmSimpleModalProps {
    modal: ConfirmModalDescriptor;
    // Internal callbacks to handle actions and resolve state
    onConfirm: () => void;
    onCancel: () => void;
    onError?: (reason: unknown) => void;
}

const confirmButtonVariant = (tone: ConfirmModalDescriptor["tone"]): ButtonProps["variant"] => {
    if (tone === "danger") {
        return "destructive";
    }
    if (tone === "warning") {
        return "destructive-outline";
    }
    return "default";
};

export function ConfirmSimpleModal({ modal, onConfirm, onCancel, onError }: ConfirmSimpleModalProps) {
    const [isLoading, setIsLoading] = useState(false);

    const handleConfirm = async () => {
        if (modal.onConfirm) {
            setIsLoading(true);
            try {
                await modal.onConfirm();
                // onConfirm() closes (unmounts) the modal, so there's no need to reset isLoading here
                onConfirm();
            } catch (error) {
                setIsLoading(false);
                onError?.(error);
            }
        } else {
            onConfirm();
        }
    };

    const handleDismiss = async () => {
        if (modal.onDismiss) {
            setIsLoading(true);
            try {
                await modal.onDismiss();
                // onCancel() closes (unmounts) the modal, so there's no need to reset isLoading here
                onCancel();
            } catch (error) {
                setIsLoading(false);
                onError?.(error);
            }
        } else {
            onCancel();
        }
    };

    return (
        <>
            {modal.body ? <div>{modal.body}</div> : null}
            <div className={"flex justify-end gap-4 max-sm:flex-col max-sm:gap-4"}>
                <Button size={"lg"} disabled={isLoading} onClick={handleDismiss} type={"button"} variant={"outline"}>
                    {modal.cancelLabel}
                </Button>
                <Button
                    size={"lg"}
                    disabled={isLoading}
                    onClick={handleConfirm}
                    type={"button"}
                    variant={confirmButtonVariant(modal.tone)}
                >
                    {isLoading ? <Loader2 className={"animate-spin"} /> : modal.confirmLabel}
                </Button>
            </div>
        </>
    );
}
